/**
 * Fjalori i vërejtjeve — një burim i vetëm për nivelet, ngjyrat dhe renditjen.
 *
 * Deri tani i njëjti fjalor jetonte në tri kopje: te faqja e raportit, te
 * shërbimi që ndërton PDF-në individuale, dhe një herë të tretë brenda vetë
 * faqes si `SEV_PDF` që nuk përdorej fare. Tri kopje do të thonë tri të vërteta
 * sapo dikush shton një nivel të katërt.
 */

/** Rendi këtu ËSHTË rendi i ashpërsisë — lehtë → rëndë. */
export const SEVERITY_LEVELS = ['Kujtesë', 'Vërejtje', 'Shkelje e rëndë'];

/**
 * Sa e rëndë është secila. Renditja te tabela e lexon këtë, jo alfabetin:
 * alfabetikisht "Shkelje e rëndë" bie mes "Kujtesë" dhe "Vërejtje", ndaj
 * kolona "Niveli" jepte një rend që nuk do të thoshte asgjë.
 */
export const SEVERITY_RANK = Object.fromEntries(SEVERITY_LEVELS.map((s, i) => [s, i + 1]));

// Çiftet bg/color janë ato të paletës së shtëpisë: gri e qetë për kujtesën,
// qelibar për vërejtjen, e kuqe për shkeljen e rëndë. Ngjyra rritet me peshën.
export const SEVERITY_META = {
  'Kujtesë': {
    color: '#475569', bg: '#F1F5F9', border: '#CBD5E1', soft: '#F8FAFC',
    label: 'KUJTESË',          rgb: [71, 85, 105],  light: [241, 245, 249], pdfBorder: [148, 163, 184],
    hint: 'Vërejtje gojore e shënuar — pa pasojë disiplinore.',
  },
  'Vërejtje': {
    color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', soft: '#FFFDF5',
    label: 'VËREJTJE ZYRTARE', rgb: [180, 83, 9],   light: [255, 251, 235], pdfBorder: [245, 158, 11],
    hint: 'Vërejtje me shkrim që hyn në dosjen e punëtorit.',
  },
  'Shkelje e rëndë': {
    color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', soft: '#FFF7F7',
    label: 'SHKELJE E RËNDË',  rgb: [185, 28, 28],  light: [254, 242, 242], pdfBorder: [239, 68, 68],
    hint: 'Shkelje që mund të çojë në masë disiplinore.',
  },
};

export const severityMeta = (s) => SEVERITY_META[s] || SEVERITY_META['Kujtesë'];

/**
 * Sjell çdo vlerë të vjetër ose të përzier te skema me tri nivele.
 *
 * Rreshtat e vjetër mbajnë "Low", "Medium", "High", "Shkelje e lehtë"… Pa këtë
 * hartë, çipat, statistikat, filtrat dhe eksportet do të shihnin secili diçka
 * tjetër për të njëjtin rresht.
 */
export function canonicalSeverity(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s || s === '—') return 'Vërejtje';
  if (/r[ëe]nd[ëe]?|grav|sever|seri|high|kritik|maj[oö]r/.test(s)) return 'Shkelje e rëndë';
  if (/shkelj|violation|breach/.test(s))                          return 'Vërejtje';
  if (/kujtes|reminder|verbal|low|minor|info|ul[ëe]t/.test(s))    return 'Kujtesë';
  return 'Vërejtje';
}

/**
 * Krahasim pa thekse dhe pa shkronja të mëdha.
 *
 * Pa këtë, kërkimi për "verejtje" nuk gjen "Vërejtje" dhe "cmimi" nuk gjen
 * "Çmimi" — pikërisht ato fjalë që përdoruesi i shkruan shpejt, pa thekse.
 */
export const normalize = (v) => String(v ?? '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

/**
 * A bie kjo vërejtje brenda periudhës së zgjedhur?
 *
 * Krahasimi bëhet mbi tekstin `yyyy-MM-dd`, jo mbi `Date`. Me `Date`, kufiri
 * "deri" ishte mesnata e asaj dite: një vërejtje e shkruar në orën 10:00 të
 * ditës së fundit binte jashtë. Pra dita e fundit e zgjedhur mungonte gjithnjë,
 * dhe askush s'e vinte re derisa numrat s'përputheshin.
 *
 * Teksti ISO renditet njësoj si data, ndaj krahasimi është i saktë dhe nuk
 * kalon fare nga zona kohore — e njëjta arsye si te `utils/period.js`.
 */
export function inRange(createdAt, from, to) {
  const day = String(createdAt || '').slice(0, 10);
  if (!day) return !from && !to;
  if (from && day < from) return false;
  if (to   && day > to)   return false;
  return true;
}

/** A e përmban ky rresht kërkimin — pa thekse, në emër, subjekt dhe përshkrim. */
export function matchesQuery(row, query) {
  const q = normalize(query);
  if (!q) return true;
  const hay = normalize(
    `${row.FirstName || ''} ${row.LastName || ''} ${row.Subject || ''} ${row.Description || ''}`
  );
  return hay.includes(q);
}
